import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, Package, AlertTriangle, Archive, 
  ChevronDown, Truck, ShoppingCart, TrendingUp, Box
} from 'lucide-react';
import { fetchReportTypes, fetchVendorReportTypes } from '../services/api';

const Navigation = ({ activeTab, onTabChange }) => {
  const [reportTypes, setReportTypes] = useState([]);
  const [vendorTypes, setVendorTypes] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);

  useEffect(() => {
    const loadTypes = async () => {
      try {
        const types = await fetchReportTypes();
        setReportTypes(types);
        const vendors = await fetchVendorReportTypes();
        setVendorTypes(vendors);
      } catch (err) {
        console.error('Error loading report types:', err);
      }
    };
    loadTypes();
  }, []);

  const mainTabs = [
    { id: 'summary', label: 'Σύνοψη', icon: LayoutDashboard },
  ];

  const reportGroups = {
    main: { label: 'Αναφορές', icon: Package },
    storage: { label: 'Αποθήκες', icon: Archive },
    special: { label: 'Ειδικές', icon: TrendingUp },
  };

  const groupedReports = reportTypes.reduce((acc, report) => {
    const group = report.group || 'main';
    if (!acc[group]) acc[group] = [];
    acc[group].push(report);
    return acc;
  }, {});

  // Group vendors by row
  const vendorRows = vendorTypes.reduce((acc, vendor) => {
    const row = vendor.row || 1;
    if (!acc[row]) acc[row] = [];
    acc[row].push(vendor);
    return acc;
  }, {});

  const handleDropdownToggle = (groupKey) => {
    setOpenDropdown(openDropdown === groupKey ? null : groupKey);
  };

  const handleReportSelect = (reportKey) => {
    onTabChange(reportKey);
    setOpenDropdown(null);
  };

  return (
    <nav className="border-b border-gray-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-1 px-4 py-2">
        {/* Main tabs */}
        {mainTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}

        {/* Report group dropdowns */}
        {Object.entries(reportGroups).map(([groupKey, groupInfo]) => {
          const reports = groupedReports[groupKey] || [];
          if (reports.length === 0) return null;
          
          const Icon = groupInfo.icon;
          const isOpen = openDropdown === groupKey;
          const hasActiveReport = reports.some(r => r.key === activeTab);

          return (
            <div key={groupKey} className="relative">
              <button
                onClick={() => handleDropdownToggle(groupKey)}
                className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  hasActiveReport
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon className="h-4 w-4" />
                {groupInfo.label}
                <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isOpen && (
                <div className="absolute left-0 top-full z-50 mt-1 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                  {reports.map((report) => (
                    <button
                      key={report.key}
                      onClick={() => handleReportSelect(report.key)}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                        activeTab === report.key ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      {report.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Vendor dropdown */}
        {vendorTypes.length > 0 && (
          <div className="relative">
            <button
              onClick={() => handleDropdownToggle('vendors')}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                vendorTypes.some(v => v.key === activeTab)
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Truck className="h-4 w-4" />
              Προμηθευτές
              <ChevronDown className={`h-4 w-4 transition-transform ${openDropdown === 'vendors' ? 'rotate-180' : ''}`} />
            </button>
            
            {openDropdown === 'vendors' && (
              <div className="absolute left-0 top-full z-50 mt-1 max-h-96 w-56 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                {Object.entries(vendorRows).map(([row, vendors]) => (
                  <div key={row}>
                    {Object.keys(vendorRows).length > 1 && (
                      <div className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase">
                        Σειρά {row}
                      </div>
                    )}
                    {vendors.map((vendor) => (
                      <button
                        key={vendor.key}
                        onClick={() => handleReportSelect(vendor.key)}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                          activeTab === vendor.key ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                      >
                        {vendor.label}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Click outside to close dropdown */}
      {openDropdown && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setOpenDropdown(null)}
        />
      )}
    </nav>
  );
};

export default Navigation;
